/**
 * deviceApiService.ts
 * 设备API服务
 * 处理与后端设备相关的API调用
 */

// 后端设备连接信息接口
export interface DeviceConnectionInfo {
  deviceId?: number;           // 设备数据库ID（可能为undefined）
  deviceIp: string;
  tcpPort: number;
  udpPort: number;
  tcpConnectTime?: number;
  lastAudioTime?: number;
  isOnline?: boolean;
  sessionId?: string;
  name?: string;               // 设备名称
  serialNumber?: number;       // 设备序列号
}

// API响应接口
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp: number;
}

/**
 * 设备API服务类
 */
class DeviceApiService {
  private baseUrl: string;

  constructor() {
    // 使用统一的API客户端，路径改为 /api/devices
    // 根据环境选择API基础URL
    const port = window.location.port;
    if (port === '12346') {
      // 开发环境：直接访问后端
      this.baseUrl = 'http://localhost:12345/api/devices';
    } else {
      // 生产环境：通过Nginx代理访问API，使用相对路径
      this.baseUrl = '/api/devices';
    }
    console.log('DeviceApiService: 初始化设备API服务 - 基础URL:', this.baseUrl);
  }

  /**
   * 获取所有设备连接信息
   * 注意：后端可能没有此端点，使用在线设备列表代替
   */
  async getAllDevices(): Promise<DeviceConnectionInfo[]> {
    try {
      console.log('DeviceApiService: 获取所有设备列表（使用在线设备列表）');
      // 后端没有 /api/devices 端点，使用在线设备列表代替
      return await this.getOnlineDevices();
    } catch (error) {
      console.error('DeviceApiService: 获取设备列表失败:', error);
      throw error;
    }
  }

  /**
   * 获取在线设备连接信息
   */
  async getOnlineDevices(): Promise<DeviceConnectionInfo[]> {
    try {
      console.log('DeviceApiService: 获取在线设备列表');
      
      const response = await fetch(`${this.baseUrl}/online`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<DeviceConnectionInfo[]> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || '获取在线设备列表失败');
      }

      console.log('DeviceApiService: 获取在线设备列表成功 - 设备数量:', result.data.length);
      return result.data;
    } catch (error) {
      console.error('DeviceApiService: 获取在线设备列表失败:', error);
      throw error;
    }
  }

  /**
   * 根据设备ID获取设备信息
   */
  async getDeviceById(deviceId: number): Promise<DeviceConnectionInfo> {
    try {
      console.log('DeviceApiService: 获取设备信息 - 设备ID:', deviceId);
      
      const response = await fetch(`${this.baseUrl}/${deviceId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<DeviceConnectionInfo> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || '获取设备信息失败');
      }

      console.log('DeviceApiService: 获取设备信息成功 - 设备ID:', deviceId);
      return result.data;
    } catch (error) {
      console.error('DeviceApiService: 获取设备信息失败 - 设备ID:', deviceId, error);
      throw error;
    }
  }


  /**
   * 添加设备（注册设备）
   */
  async addDevice(deviceInfo: {
    deviceIp: string;
    tcpPort: number;
    name?: string;
  }): Promise<DeviceConnectionInfo> {
    try {
      console.log('DeviceApiService: 注册设备 - IP:', deviceInfo.deviceIp);
      
      // 使用后端的注册设备API
      const response = await fetch(`${this.baseUrl}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          deviceIp: deviceInfo.deviceIp,
          tcpPort: deviceInfo.tcpPort,
          name: deviceInfo.name
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<DeviceConnectionInfo> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || '注册设备失败');
      }

      console.log('DeviceApiService: 注册设备成功 - IP:', deviceInfo.deviceIp);
      return result.data;
    } catch (error) {
      console.error('DeviceApiService: 注册设备失败 - IP:', deviceInfo.deviceIp, error);
      throw error;
    }
  }

  /**
   * 设置设备离线（断开设备连接）
   */
  async setDeviceOffline(deviceId: number): Promise<void> {
    try {
      console.log('DeviceApiService: 断开设备连接 - 设备ID:', deviceId, '类型:', typeof deviceId);
      console.log('DeviceApiService: API URL:', `${this.baseUrl}/${deviceId}/disconnect`);
      
      const token = this.getAuthToken();
      console.log('DeviceApiService: 认证Token:', token ? '存在' : '不存在');
      
      // 使用后端的断开设备连接API
      const response = await fetch(`${this.baseUrl}/${deviceId}/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('DeviceApiService: 响应状态:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DeviceApiService: HTTP错误响应:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result: ApiResponse<void> = await response.json();
      console.log('DeviceApiService: 响应数据:', result);
      
      if (!result.success) {
        throw new Error(result.message || '断开设备连接失败');
      }

      console.log('DeviceApiService: 断开设备连接成功 - 设备ID:', deviceId);
    } catch (error) {
      console.error('DeviceApiService: 断开设备连接失败 - 设备ID:', deviceId, error);
      throw error;
    }
  }

  /**
   * 移除设备（断开设备连接）
   * 注意：后端没有删除设备的API，使用断开连接代替
   */
  async removeDevice(deviceId: number): Promise<void> {
    try {
      console.log('DeviceApiService: 移除设备（断开连接） - 设备ID:', deviceId);
      // 后端没有删除设备的API，使用断开连接代替
      await this.setDeviceOffline(deviceId);
    } catch (error) {
      console.error('DeviceApiService: 移除设备失败 - 设备ID:', deviceId, error);
      throw error;
    }
  }

  /**
   * 获取认证Token
   */
  private getAuthToken(): string {
    // 从localStorage获取token，使用与authService一致的key
    const token = localStorage.getItem('token') || '';
    return token;
  }
}

// 创建单例实例
export const deviceApiService = new DeviceApiService();
